import React, { useState, useEffect } from 'react';
import { savedNotes } from '../api';
import PerformanceBadge from '../PerformanceBadge';
import NoteTypeTag from '../NoteTypeTag';
import RichTextEditor from './RichTextEditor';
import FormattedContent from './FormattedContent';
import UnassignedPocketNotesSection from './UnassignedPocketNotesSection';
import AssignedPocketNotesSection from './AssignedPocketNotesSection';
import { getPlainText } from '../utils/htmlText';

function SavedNotes({
  allStudents,
  formatDateTime,
  showAlertModal,
  userId,
  onPocketNoteAssigned,
  pocketEnabled = false // Pocket sections are for the head coach only (see utils/features.js)
}) {
  // Bumped whenever a note is unassigned (in AssignedPocketNotesSection)
  // so UnassignedPocketNotesSection - which fetches its own list
  // independently and has no other way to know a new entry just appeared -
  // refetches too, instead of staying stale until the page reloads.
  const [unassignedRefreshTrigger, setUnassignedRefreshTrigger] = useState(0);
  const [savedNotesList, setSavedNotesList] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newNoteBadge, setNewNoteBadge] = useState(null);
  const [newNoteType, setNewNoteType] = useState(null);
  const [sharingNoteId, setSharingNoteId] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSavedNotes();
  }, []);

  const loadSavedNotes = async () => {
    try {
      const response = await savedNotes.getAll();
      
      // Handle different response formats
      const notesData = Array.isArray(response?.data) 
        ? response.data 
        : Array.isArray(response) 
          ? response 
          : [];
      
      setSavedNotesList(notesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading saved notes:', error);
      setSavedNotesList([]);
      setLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!getPlainText(newNote)) return;
    
    try {
      
      const response = await savedNotes.create({
        content: newNote,
        performanceBadge: newNoteBadge,
        noteType: newNoteType
      });

      // Reset form
      setNewNote('');
      setNewNoteBadge(null);
      setNewNoteType(null);
      
      // Reload
      await loadSavedNotes();
      
      if (showAlertModal) {
        showAlertModal('Success', 'Note saved! You can share it later.');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      console.error('Error details:', error.response?.data || error.message);
      if (showAlertModal) {
        showAlertModal('Error', `Failed to save note: ${error.response?.data?.message || error.message}`);
      } else {
        alert('Failed to save note: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleShareNote = async (noteId) => {
    if (selectedStudents.length === 0) {
      showAlertModal('Select Students', 'Please select at least one student');
      return;
    }
    
    try {
      // Share the note to selected students
      await savedNotes.shareToStudent(noteId, selectedStudents);
      
      showAlertModal('Success', `Note shared to ${selectedStudents.length} student(s)!`);
      
      // Close modal
      setSharingNoteId(null);
      setSelectedStudents([]);
      setSearchQuery('');
      
      // Reload to update shared count
      await loadSavedNotes();
    } catch (error) {
      console.error('Error sharing note:', error);
      showAlertModal('Error', 'Failed to share note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this saved note?')) return;
    
    try {
      await savedNotes.delete(noteId);
      await loadSavedNotes();
      showAlertModal('Success', 'Note deleted');
    } catch (error) {
      console.error('Error deleting note:', error);
      showAlertModal('Error', 'Failed to delete note');
    }
  };

  const toggleStudentSelection = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const filteredStudents = allStudents.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '40px'
      }}>
        {/* Animated Spinner */}
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid rgba(47, 75, 162, 0.2)',
          borderTop: '4px solid #2f4ba2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }} />
        
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#2f4ba2',
          marginBottom: '8px',
          animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          Loading Saved Notes...
        </div>
        
        <div style={{
          fontSize: '14px',
          color: '#95a5a6'
        }}>
          Please wait
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px',
      maxWidth: '900px',
      margin: '0 auto',
      paddingBottom: '100px'
    }}>
      {/* 🎙️ POCKET API: Unassigned Notes Section - lives here, in the
          generic top-level Notes tab, rather than the Students dashboard
          or a specific student's page, since it isn't tied to any one
          student until the coach assigns it. */}
      {pocketEnabled && (<>
      <div style={{ marginBottom: '24px' }}>
        <UnassignedPocketNotesSection
          allPlayers={allStudents}
          userId={userId}
          onAssignmentSuccess={onPocketNoteAssigned}
          refreshTrigger={unassignedRefreshTrigger}
        />
      </div>

      {/* Assigned Pocket Summaries - every already-assigned summary across
          all students, right below Unassigned, so the coach has one place
          to review and (if needed) send one back to unassigned. Students
          never see this - to them the note just appears or disappears
          like any other. */}
      <div style={{ marginBottom: '24px' }}>
        <AssignedPocketNotesSection
          allStudents={allStudents}
          onAssignedMore={onPocketNoteAssigned}
          notes={(allStudents || []).flatMap(student =>
            (student.notes || [])
              .filter(n => n.noteType === 'pocket_recording')
              .map(n => ({ ...n, studentId: student._id, studentName: student.name }))
          )}
          onUnassigned={() => {
            if (onPocketNoteAssigned) onPocketNoteAssigned();
            // Unassigning creates a brand-new unassigned entry that
            // UnassignedPocketNotesSection has no other way to learn
            // about, since it fetches its own list independently.
            setUnassignedRefreshTrigger(t => t + 1);
          }}
          onRefreshed={onPocketNoteAssigned}
        />
      </div>
      </>)}

      {/* Write New Note Section */}
      <div style={{
        marginBottom: '30px',
        // 'white' (not '#ffffff') - see the Saved Notes card below for why.
        background: 'white',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '2px solid #ecf0f1'
      }}>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '700',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#2c3e50'
        }}>
          <span style={{ fontSize: '24px' }}>✍️</span>
          Write & Save Note
        </h3>

        <div style={{ marginBottom: '16px' }}>
          <RichTextEditor
            value={newNote}
            onChange={setNewNote}
            maxCharacters={2000}
            placeholder="Write a note to save for later..."
          />
        </div>

        {/* Badge Selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#2c3e50', 
            display: 'block', 
            marginBottom: '8px' 
          }}>
            Performance Badge (Optional)
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { value: null, label: 'None', color: '#95a5a6' },
              { value: 'excellent', label: '⭐ Excellent', color: '#FFD700' },
              { value: 'good', label: '👍 Good', color: '#27ae60' },
              { value: 'average', label: '👌 Average', color: '#3498db' },
              { value: 'needs_work', label: '📈 Needs Work', color: '#e67e22' },
              { value: 'keep_trying', label: '💪 Keep Trying', color: '#9b59b6' }
            ].map(badge => (
              <button
                key={badge.value || 'none'}
                onClick={() => setNewNoteBadge(badge.value)}
                style={{
                  padding: '8px 16px',
                  background: newNoteBadge === badge.value ? badge.color : 'white',
                  color: newNoteBadge === badge.value ? 'white' : '#2c3e50',
                  border: `2px solid ${badge.color}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
              >
                {badge.label}
              </button>
            ))}
          </div>
        </div>

        {/* Type Selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#2c3e50', 
            display: 'block', 
            marginBottom: '8px' 
          }}>
            Note Type (Optional)
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { value: null, label: 'None', color: '#95a5a6' },
              { value: 'lesson', label: '📚 Lesson', color: '#3498db' },
              { value: 'wisdom', label: '💡 Wisdom', color: '#f39c12' }
            ].map(type => (
              <button
                key={type.value || 'none'}
                onClick={() => setNewNoteType(type.value)}
                style={{
                  padding: '8px 16px',
                  background: newNoteType === type.value ? type.color : 'white',
                  color: newNoteType === type.value ? 'white' : '#2c3e50',
                  border: `2px solid ${type.color}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handleSaveNote}
          disabled={!getPlainText(newNote)}
          style={{
            width: '100%',
            padding: '14px',
            background: getPlainText(newNote)
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : '#e0e0e0',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: getPlainText(newNote) ? 'pointer' : 'not-allowed',
            fontSize: '16px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: getPlainText(newNote) ? 1 : 0.6,
            transition: 'all 0.3s ease',
            boxShadow: getPlainText(newNote) ? '0 4px 15px rgba(47, 75, 162, 0.4)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (getPlainText(newNote)) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(47, 75, 162, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (getPlainText(newNote)) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(47, 75, 162, 0.4)';
            }
          }}
        >
          <span style={{ fontSize: '18px' }}>💾</span>
          <span>Save for Later</span>
        </button>
      </div>

      {/* Saved Notes List */}
      <div>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '700',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#2c3e50'
        }}>
          <span style={{ fontSize: '24px' }}>📋</span>
          Saved Notes ({savedNotesList.length})
        </h3>

        {savedNotesList.length === 0 ? (
          <div style={{
            background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
            padding: '60px 40px',
            borderRadius: '20px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 10px 40px rgba(47, 75, 162, 0.3)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📝</div>
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
              No saved notes yet
            </div>
            <div style={{ fontSize: '16px', opacity: 0.9 }}>
              Write a note above and save it for later
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {savedNotesList.map(note => (
              <div
                key={note._id}
                style={{
                  // 'white' (not '#ffffff') so this card matches the
                  // global dark-theme sweep in darkTheme.css, which only
                  // matches the literal "white" keyword (and a specific
                  // list of near-white shades) in inline styles - #ffffff
                  // fell outside that list, so this card stayed pure white
                  // while its own inline text color (below) still got
                  // swept light, going washed-out/illegible in dark theme.
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid #ecf0f1',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                }}
              >
                {/* Accent bar based on badge */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: note.performanceBadge 
                    ? {
                        excellent: 'linear-gradient(90deg, #FFD700 0%, #FFA500 100%)',
                        good: 'linear-gradient(90deg, #27ae60 0%, #2ecc71 100%)',
                        average: 'linear-gradient(90deg, #3498db 0%, #5dade2 100%)',
                        needs_work: 'linear-gradient(90deg, #e67e22 0%, #f39c12 100%)',
                        keep_trying: 'linear-gradient(90deg, #9b59b6 0%, #8e44ad 100%)'
                      }[note.performanceBadge]
                    : 'linear-gradient(90deg, #2f4ba2 0%, #14246b 100%)',
                  borderRadius: '16px 16px 0 0'
                }} />

                {/* Badges and Tags */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '12px',
                  flexWrap: 'wrap'
                }}>
                  {note.performanceBadge && (
                    <PerformanceBadge level={note.performanceBadge} size="small" />
                  )}
                  {note.noteType && (
                    <NoteTypeTag type={note.noteType} size="small" />
                  )}
                </div>

                {/* Note Content */}
                <div style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: '#2c3e50',
                  marginBottom: '16px',
                  wordBreak: 'break-word',
                  paddingLeft: '16px',
                  borderLeft: '4px solid #2f4ba2'
                }}>
                  <FormattedContent content={note.content} />
                </div>

                {/* Metadata */}
                <div style={{
                  fontSize: '13px',
                  color: '#95a5a6',
                  marginBottom: '16px',
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>📅</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                  </span>
                  {note.sharedWith?.length > 0 && (
                    <span style={{
                      background: '#e8f5e9',
                      color: '#27ae60',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>✅</span>
                      <span>Shared with {note.sharedWith.length} student(s)</span>
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setSharingNoteId(note._id)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(39, 174, 96, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>📤</span>
                    <span>Share to Student</span>
                  </button>

                  <button
                    onClick={() => handleDeleteNote(note._id)}
                    style={{
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '700',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(231, 76, 60, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(231, 76, 60, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>🗑️</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share Modal */}
      {sharingNoteId && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              animation: 'fadeIn 0.3s ease-out',
              padding: '20px'
            }}
            onClick={() => {
              setSharingNoteId(null);
              setSelectedStudents([]);
              setSearchQuery('');
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '32px',
                width: '100%',
                maxWidth: '600px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px',
                paddingBottom: '16px',
                borderBottom: '2px solid #ecf0f1'
              }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '22px', 
                  fontWeight: '700',
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '24px' }}>📤</span>
                  Share Note To Students
                </h3>
                <button
                  onClick={() => {
                    setSharingNoteId(null);
                    setSelectedStudents([]);
                    setSearchQuery('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: '#95a5a6',
                    lineHeight: '1'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Search */}
              <input
                type="text"
                placeholder="🔍 Search students by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '2px solid #ecf0f1',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2f4ba2'}
                onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
              />

              {/* Student List */}
              <div style={{ 
                maxHeight: '350px', 
                overflowY: 'auto',
                marginBottom: '20px',
                border: '1px solid #ecf0f1',
                borderRadius: '12px',
                padding: '8px'
              }}>
                {filteredStudents.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#95a5a6'
                  }}>
                    No students found
                  </div>
                ) : (
                  filteredStudents.map(student => (
                    <div
                      key={student._id}
                      onClick={() => toggleStudentSelection(student._id)}
                      style={{
                        padding: '14px',
                        background: selectedStudents.includes(student._id) ? '#e3f2fd' : 'white',
                        border: `2px solid ${selectedStudents.includes(student._id) ? '#3498db' : 'transparent'}`,
                        borderRadius: '10px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedStudents.includes(student._id)) {
                          e.currentTarget.style.background = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedStudents.includes(student._id)) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{
                        width: '22px',
                        height: '22px',
                        border: `2px solid ${selectedStudents.includes(student._id) ? '#3498db' : '#95a5a6'}`,
                        borderRadius: '6px',
                        background: selectedStudents.includes(student._id) ? '#3498db' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        {selectedStudents.includes(student._id) && '✓'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: '600', 
                          fontSize: '15px',
                          color: '#2c3e50',
                          marginBottom: '2px'
                        }}>
                          {student.name}
                        </div>
                        <div style={{ 
                          fontSize: '13px', 
                          color: '#95a5a6' 
                        }}>
                          {student.email}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Selected Count */}
              {selectedStudents.length > 0 && (
                <div style={{
                  background: '#e3f2fd',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#3498db',
                  textAlign: 'center'
                }}>
                  {selectedStudents.length} student(s) selected
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setSharingNoteId(null);
                    setSelectedStudents([]);
                    setSearchQuery('');
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '15px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={() => handleShareNote(sharingNoteId)}
                  disabled={selectedStudents.length === 0}
                  style={{
                    flex: 2,
                    padding: '14px',
                    background: selectedStudents.length > 0 
                      ? 'linear-gradient(135deg, #27ae60 0%, #229954 100%)' 
                      : '#e0e0e0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: selectedStudents.length > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '700',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    opacity: selectedStudents.length > 0 ? 1 : 0.6,
                    boxShadow: selectedStudents.length > 0 
                      ? '0 4px 15px rgba(39, 174, 96, 0.4)' 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedStudents.length > 0) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(39, 174, 96, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedStudents.length > 0) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(39, 174, 96, 0.4)';
                    }
                  }}
                >
                  Send to {selectedStudents.length} Student(s)
                </button>
              </div>
            </div>
          </div>

          {/* Animations */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(30px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
        </>
      )}
    </div>
  );
}

export default SavedNotes;